import { useMemo, useState, type ReactNode } from 'react';
import {
  listSystemWorkspaces,
} from '@/governor/system-workspaces/SystemWorkspaceRegistry';
import {
  projectSystemStatus,
} from '@/governor/system-workspaces/SystemStatusProjectionEngine';
import {
  projectTreeWorkspace,
} from '@/governor/system-workspaces/TreeWorkspaceProjection';
import MembershipBillingOperationsWorkspace from '@/components/governor/MembershipBillingOperationsWorkspace';
import CallsOperationsWorkspace from '@/components/governor/CallsOperationsWorkspace';
import TreeOperationsWorkspace from '@/components/governor/TreeOperationsWorkspace';
import type {
  CertificationState,
  SystemWorkspaceDefinition,
} from '@/governor/system-workspaces/SystemWorkspaceContracts';

const certificationLabels: Record<
  keyof SystemWorkspaceDefinition['certification'],
  string
> = {
  source: 'Source',
  build: 'Build',
  simulatedRuntime: 'Simulated runtime',
  deployedRuntime: 'Deployed runtime',
  liveTransaction: 'Live transaction',
  recovery: 'Recovery',
};

function certificationClass(
  state: CertificationState,
): string {
  if (state === 'pass') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  }

  if (state === 'fail') {
    return 'border-rose-200 bg-rose-50 text-rose-800';
  }

  if (state === 'pending') {
    return 'border-amber-200 bg-amber-50 text-amber-800';
  }

  return 'border-slate-200 bg-slate-50 text-slate-700';
}

function statusLightClass(
  light:
    ReturnType<
      typeof projectSystemStatus
    >['light'],
): string {
  if (light === 'green') {
    return 'border-emerald-300 bg-emerald-100 text-emerald-900';
  }

  if (light === 'orange') {
    return 'border-orange-300 bg-orange-100 text-orange-900';
  }

  if (light === 'red') {
    return 'border-rose-300 bg-rose-100 text-rose-900';
  }

  if (light === 'blue') {
    return 'border-sky-300 bg-sky-100 text-sky-900';
  }

  if (light === 'purple') {
    return 'border-violet-300 bg-violet-100 text-violet-900';
  }

  return 'border-slate-300 bg-slate-100 text-slate-800';
}

function statusDotClass(
  light:
    ReturnType<
      typeof projectSystemStatus
    >['light'],
): string {
  if (light === 'green') {
    return 'bg-emerald-500';
  }

  if (light === 'orange') {
    return 'bg-orange-500';
  }

  if (light === 'red') {
    return 'bg-rose-600';
  }

  if (light === 'blue') {
    return 'bg-sky-500';
  }

  if (light === 'purple') {
    return 'bg-violet-600';
  }

  return 'bg-slate-400';
}

function WorkspaceSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <details className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <summary className="cursor-pointer font-bold text-slate-950">
        {title}
      </summary>

      <div className="mt-4">
        {children}
      </div>
    </details>
  );
}

function SystemWorkspaceShell({
  workspace,
  treeRepairPlan,
  treeLiveOutput,
  onWorkspaceChange,
}: {
  workspace: SystemWorkspaceDefinition;
  treeRepairPlan: any;
  treeLiveOutput: any;
  onWorkspaceChange?: () => void;
}) {
  const projectedTree =
    workspace.id === 'tree'
      ? projectTreeWorkspace(workspace)
      : null;

  const displayedWorkspace =
    projectedTree?.workspace ?? workspace;

  const authoritativeSources =
    displayedWorkspace.authoritySources.filter(
      source => source.authoritative,
    );

  const systemStatus =
    projectedTree?.status ??
    projectSystemStatus(displayedWorkspace);

  return (
    <details
      className="mt-5 rounded-[1.75rem] border border-violet-200 bg-white p-5"
    >
      <summary className="cursor-pointer">
        <span className="flex items-center justify-between gap-3">
          <span className="text-xl font-bold text-slate-950">
            {workspace.name} workspace
          </span>

          <span
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold uppercase ${statusLightClass(
              systemStatus.light,
            )}`}
            title={systemStatus.reason}
          >
            <span
              aria-hidden="true"
              className={`h-2.5 w-2.5 rounded-full ${statusDotClass(
                systemStatus.light,
              )}`}
            />
            {systemStatus.label}
          </span>
        </span>
      </summary>

      <section
        aria-label={`${workspace.name} workspace`}
        className="mt-5"
      >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-violet-700">
            System workspace
          </p>

          <h3 className="mt-2 text-2xl font-bold text-slate-950">
            {workspace.name}
          </h3>

          <p className="mt-2 max-w-3xl text-slate-600">
            {workspace.description}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <span
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold uppercase ${statusLightClass(
              systemStatus.light,
            )}`}
            title={systemStatus.reason}
          >
            <span
              aria-hidden="true"
              className={`h-2.5 w-2.5 rounded-full ${statusDotClass(
                systemStatus.light,
              )}`}
            />
            {systemStatus.label}
          </span>

          <span className="rounded-full bg-violet-100 px-3 py-1 text-xs font-bold text-violet-800">
            {workspace.owningGovernor}
          </span>
        </div>
      </div>

      <div
        className={`mt-5 rounded-2xl border p-4 ${statusLightClass(
          systemStatus.light,
        )}`}
      >
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className={`h-4 w-4 rounded-full ${statusDotClass(
              systemStatus.light,
            )}`}
          />

          <div>
            <p className="text-xs font-bold uppercase tracking-wider">
              System status
            </p>

            <p className="mt-1 text-lg font-bold">
              {systemStatus.label}
            </p>
          </div>
        </div>

        <p className="mt-3 text-sm">
          {systemStatus.reason}
        </p>
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-3">
        <WorkspaceSection title="Authority">

          <p className="mt-2 font-medium text-slate-900">
            {workspace.authoritySummary}
          </p>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {authoritativeSources.map(source => (
              <div
                key={source.id}
                className="rounded-xl border border-slate-200 bg-white p-3"
              >
                <p className="text-sm font-bold text-slate-900">
                  {source.label}
                </p>

                <p className="mt-1 break-words text-xs text-slate-600">
                  {source.reference}
                </p>

                <p className="mt-2 text-xs font-bold uppercase text-violet-700">
                  {source.kind.replaceAll('_', ' ')}
                </p>
              </div>
            ))}
          </div>
        </WorkspaceSection>

        <WorkspaceSection title="Controlled environment">

          <div className="mt-3 space-y-2 text-sm text-slate-700">
            <p>
              Production writes:{' '}
              <b>
                {workspace.simulatorPolicy
                  .productionWritesAllowed
                  ? 'Allowed'
                  : 'Blocked'}
              </b>
            </p>

            <p>
              AI ceiling:{' '}
              <b>
                Level{' '}
                {
                  workspace.aiPolicy
                    .maximumAutonomousLevel
                }
              </b>
            </p>

            <p>
              AI production execution:{' '}
              <b>
                {workspace.aiPolicy
                  .mayExecuteProductionWrites
                  ? 'Allowed'
                  : 'Blocked'}
              </b>
            </p>

            <p>
              Evidence namespace:{' '}
              <b className="break-all">
                {
                  workspace.simulatorPolicy
                    .evidenceNamespace
                }
              </b>
            </p>
          </div>

          <div className="mt-4 rounded-xl border border-violet-200 bg-violet-50 p-3 text-sm text-violet-900">
            Simulator and AI engines are installed and governed.
            Membership-specific scenarios and assistants remain
            unavailable until their registered packages are certified.
          </div>
        </WorkspaceSection>
      </div>

      <div className="mt-5">
        <WorkspaceSection title="Certification">

        <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {Object.entries(
            displayedWorkspace.certification,
          ).map(([key, value]) => (
            <div
              key={key}
              className={`rounded-xl border p-3 ${certificationClass(
                value,
              )}`}
            >
              <p className="text-xs font-bold uppercase tracking-wide">
                {
                  certificationLabels[
                    key as keyof typeof certificationLabels
                  ]
                }
              </p>

              <p className="mt-1 text-lg font-bold uppercase">
                {value.replaceAll('_', ' ')}
              </p>
            </div>
          ))}
        </div>
        </WorkspaceSection>
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <WorkspaceSection title="Dependencies">

          <div className="mt-3 space-y-3">
            {workspace.dependencies.map(dependency => (
              <div
                key={`${dependency.systemId}-${dependency.relationship}`}
                className="rounded-xl bg-slate-50 p-3"
              >
                <p className="font-bold text-slate-900">
                  {dependency.systemId}
                </p>

                <p className="text-xs font-bold uppercase text-violet-700">
                  {dependency.relationship}
                </p>

                <p className="mt-1 text-sm text-slate-600">
                  {dependency.reason}
                </p>
              </div>
            ))}
          </div>
        </WorkspaceSection>

        <WorkspaceSection title="Milestone">

          <p className="mt-3 font-bold text-slate-950">
            {workspace.milestone.current}
          </p>

          <p className="mt-4 text-xs font-bold uppercase tracking-wide text-slate-500">
            Completed
          </p>

          <p className="mt-1 text-sm text-slate-700">
            {workspace.milestone.completed.join(', ')}
          </p>

          <p className="mt-4 text-xs font-bold uppercase tracking-wide text-slate-500">
            Pending
          </p>

          <p className="mt-1 text-sm text-slate-700">
            {workspace.milestone.pending.join(', ')}
          </p>
        </WorkspaceSection>
      </div>

      {workspace.id === 'tree' ? (
        <TreeOperationsWorkspace
          repairPlan={treeRepairPlan}
          liveOutput={treeLiveOutput}
          onSimulationChange={onWorkspaceChange}
        />
      ) : workspace.id === 'membership-billing' ? (
        <MembershipBillingOperationsWorkspace
          workspace={workspace}
        />
      ) : workspace.id === 'calls' ? (
        <CallsOperationsWorkspace
          workspace={workspace}
        />
      ) : (
        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            'Runtime evidence',
            'Controlled simulator',
            'AI assistance',
            'Incidents & repairs',
          ].map(label => (
            <div
              key={label}
              className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4"
            >
              <p className="font-bold text-slate-800">
                {label}
              </p>

              <p className="mt-1 text-sm text-slate-500">
                Foundation registered. Engine wiring pending.
              </p>
            </div>
          ))}
        </div>
      )}
      </section>
    </details>
  );
}

export default function SystemWorkspaceDirectory({
  treeRepairPlan,
  treeLiveOutput,
}: {
  treeRepairPlan: any;
  treeLiveOutput: any;
}) {
  const workspaces = useMemo(
    () => listSystemWorkspaces(),
    [],
  );

  const [
    ,
    setWorkspaceRevision,
  ] = useState(0);

  const [selectedSystemId, setSelectedSystemId] =
    useState<string>(
      workspaces[0]?.id ?? '',
    );

  const selectedWorkspace =
    workspaces.find(
      workspace =>
        workspace.id === selectedSystemId,
    ) ??
    workspaces[0] ??
    null;

  return (
    <section className="my-6 rounded-[2rem] border border-violet-200 bg-[#f7f3ff] p-5 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-violet-700">
            Governed system operations
          </p>

          <h2 className="mt-2 text-3xl font-bold text-slate-950">
            System Operations Directory
          </h2>

          <p className="mt-2 max-w-3xl text-slate-600">
            Each KinsCircle system receives one accountable space for
            authority, evidence, simulation, AI assistance,
            certification, incidents, and repair.
          </p>
        </div>

        <div className="rounded-xl border border-violet-200 bg-white px-4 py-3 text-sm text-slate-700">
          Registered systems:{' '}
          <b>{workspaces.length}</b>
        </div>
      </div>

      {workspaces.length === 0 ? (
        <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
          No governed system workspace is registered.
        </div>
      ) : (
        <>
          <div
            aria-label="Registered KinsCircle systems"
            className="mt-5 space-y-3"
          >
            {workspaces.map(workspace => {
              const selected =
                workspace.id ===
                selectedWorkspace?.id;

              const systemStatus =
                projectSystemStatus(
                  workspace,
                );

              return (
                <button
                  key={workspace.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() =>
                    setSelectedSystemId(
                      workspace.id,
                    )
                  }
                  className={
                    'w-full rounded-xl border px-4 py-3 text-left transition ' +
                    (selected
                      ? 'border-violet-500 bg-white/80 ring-2 ring-violet-200'
                      : 'border-slate-300 bg-transparent hover:border-violet-400 hover:bg-white/60')
                  }
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-bold text-slate-950">
                      {workspace.name}
                    </p>

                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[0.65rem] font-bold uppercase ${statusLightClass(
                        systemStatus.light,
                      )}`}
                      title={
                        systemStatus.reason
                      }
                    >
                      <span
                        aria-hidden="true"
                        className={`h-2 w-2 rounded-full ${statusDotClass(
                          systemStatus.light,
                        )}`}
                      />
                      {systemStatus.label}
                    </span>
                  </div>

                  <div className="mt-2 flex items-center justify-between gap-3">
                    <p className="text-sm text-slate-600">
                      {workspace.owningGovernor}
                    </p>

                    <p className="text-xs font-bold uppercase text-violet-700">
                      Open workspace →
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {selectedWorkspace && (
            <SystemWorkspaceShell
              workspace={selectedWorkspace}
              treeRepairPlan={treeRepairPlan}
              treeLiveOutput={treeLiveOutput}
              onWorkspaceChange={() =>
                setWorkspaceRevision(
                  value => value + 1,
                )
              }
            />
          )}
        </>
      )}

      <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
        Legacy Command Center panels remain below this directory until
        each responsibility is mapped, migrated, and verified inside
        its owning system workspace.
      </div>
    </section>
  );
}
